import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import Message from '@ui/message/Message';
import FormPlaceholder from '@ui/placeholder/FormPlaceholder';
import { Integration } from '@app/model/integration-settings/Integration';
import { useCompanyHotelIntegrations } from '@app/model/integration-settings/service/IntegrationSettingsHooks';
import { findIntegration } from './IntegrationSettings.utilities';
import IntegrationSettingsAccess, { useIntegrationSettingsAllowed } from './IntegrationSettingsAccess';
import AvailabilityCard from './card/AvailabilityCard';
import ReservationsCard from './card/ReservationsCard';
import PricingCard from './card/PricingCard';
import IntegrationLogDrawer, { IntegrationLogDrawerHandle } from './overlay/IntegrationLogDrawer';

// The page of one property (Direction A): three cards, one per Integration Type. A cell of the overview opens the
// page at its card (#availability, #reservations, #pricing).
const IntegrationsPropertyPage = React.memo(() => {
	const companyHotelId = Number(useParams<{ companyHotelId: string }>().companyHotelId);
	const { hash } = useLocation();
    const { isAllowed } = useIntegrationSettingsAllowed();
    const { data, isLoading, isError } = useCompanyHotelIntegrations(companyHotelId, isAllowed);
	const log = useRef<IntegrationLogDrawerHandle>(null);

	useEffect(() => {
		if (data !== undefined && hash !== '') document.getElementById(hash.slice(1))?.scrollIntoView();
	}, [data, hash]);

	const openLog = (integration: Integration) => () => log.current?.open(integration);
	const availability = data === undefined ? undefined : findIntegration(data.integrations, 'AVAILABILITY');
	const reservations = data === undefined ? undefined : findIntegration(data.integrations, 'RESERVATIONS');
	const pricing = data === undefined ? undefined : findIntegration(data.integrations, 'PRICING');

	return <IntegrationSettingsAccess>
		<nav aria-label="Breadcrumb" className="mb-5 flex items-center gap-x-2 text-sm">
			<Link to="/secured/settings/integrations" className="font-medium text-gray-500 hover:text-gray-700">Integrations</Link>
			<ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
			<span className="font-semibold text-gray-900">{data?.name ?? '…'}</span>
		</nav>
		{isLoading && <FormPlaceholder />}
		{isError && <Message state="error" title="The integrations of this property did not load">
			Go back to the list and open the property again.
		</Message>}
		{data !== undefined && <div className="flex flex-col gap-y-6">
			{availability !== undefined && <AvailabilityCard key={`a-${availability.id}`} integration={availability} onOpenLog={openLog(availability)} />}
			{reservations !== undefined && <ReservationsCard key={`r-${reservations.id}`} integration={reservations} onOpenLog={openLog(reservations)} />}
			{pricing !== undefined && <PricingCard key={`p-${pricing.id}-${pricing.status}`} integration={pricing} onOpenLog={openLog(pricing)} />}
		</div>}
		<IntegrationLogDrawer ref={log} hotelName={data?.name ?? ''} />

	</IntegrationSettingsAccess>;
});

export default IntegrationsPropertyPage;
